class FeaturedCollectionsTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.panels = [];
  }

  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll(':scope > [role="tablist"] > [role="tab"]'));
    this.panels = Array.from(this.querySelectorAll(':scope > .featured-collections-products > [role="tabpanel"]'));
    this.magic = this.querySelector('.featured-collections-header-magic');

    if (this.tabs.length === 0 || this.panels.length === 0) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.activateTab(index, true);
      });

      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    // Below the md breakpoint, tabs flagged with data-hide-mobile are hidden by CSS.
    this.desktopQuery = window.matchMedia(window.theme?.mediaQueries?.md || '(min-width: 768px)');

    this.activeIndex = Math.max(
      0,
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    // Drop the server-rendered mobile pre-selection; from here on aria-selected/hidden rule.
    this.tabs.forEach((tab) => tab.classList.remove('featured-collections-tab--mobile-default'));
    this.panels.forEach((panel) => panel.classList.remove('featured-collections-panel--mobile-default'));
    this.activateTab(this.getVisibleIndex(this.activeIndex), false);

    // Crossing the breakpoint can hide the active tab — fall back to the first visible one.
    this.onBreakpointChange = () => {
      const visibleIndex = this.getVisibleIndex(this.activeIndex);
      if (visibleIndex !== this.activeIndex) {
        this.activateTab(visibleIndex, false);
      } else {
        this.moveMagic(this.activeIndex, false);
      }
    };
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);

    // Reposition the magic indicator after fonts load and on resize (tab widths shift).
    // Snap (no animation) — these aren't user-initiated tab changes.
    this.onResize = () => this.moveMagic(this.activeIndex, false);
    window.addEventListener('resize', this.onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.moveMagic(this.activeIndex, false));
    }
  }

  disconnectedCallback() {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onBreakpointChange) {
      this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    }
  }

  /**
   * Whether the tab at the given index is shown at the current viewport width.
   * @param {number} index - Tab index.
   * @returns {boolean}
   */
  isTabVisible(index) {
    const tab = this.tabs[index];
    if (!tab) return false;
    return this.desktopQuery.matches || !tab.hasAttribute('data-hide-mobile');
  }

  /**
   * Returns the given index if that tab is visible, otherwise the first visible tab's index.
   * @param {number} index - Preferred tab index.
   * @returns {number}
   */
  getVisibleIndex(index) {
    if (this.isTabVisible(index)) return index;
    const firstVisible = this.tabs.findIndex((tab, i) => this.isTabVisible(i));
    return firstVisible === -1 ? index : firstVisible;
  }

  /**
   * Slides the magic indicator to sit behind the active tab.
   * Only the horizontal position/width are JS-driven; the height comes from CSS
   * (top/bottom: 0) so the indicator is correctly sized on first paint — no load flash.
   * @param {number} index - Index of the active tab.
   * @param {boolean} [animate=true] - When false, snaps without transition (initial/resize).
   */
  moveMagic(index, animate = true) {
    if (!this.magic) return;
    const tab = this.tabs[index];
    if (!tab) return;

    const parent = this.magic.offsetParent;
    const tabRect = tab.getBoundingClientRect();
    let left = tab.offsetLeft;

    if (parent) {
      const parentRect = parent.getBoundingClientRect();
      // Offset from the parent padding box (the indicator's containing block).
      left = tabRect.left - parentRect.left - parent.clientLeft;
    }

    if (!animate) this.magic.style.transition = 'none';
    this.magic.style.width = `${tabRect.width}px`;
    this.magic.style.transform = `translateX(${left}px)`;
    if (!animate) {
      // Force reflow so the snap applies, then restore the CSS transition.
      void this.magic.offsetWidth;
      this.magic.style.transition = '';
    }
  }

  activateTab(index, setFocus) {
    if (index < 0 || index >= this.tabs.length) return;

    this.activeIndex = index;

    this.tabs.forEach((tab, i) => {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      tab.classList.toggle('is-active', isSelected);
    });

    this.panels.forEach((panel, i) => {
      const isSelected = i === index;
      panel.toggleAttribute('hidden', !isSelected);
      panel.classList.toggle('is-active', isSelected);
    });

    // Animate only on user-initiated changes (click/keyboard set focus);
    // the initial activation (setFocus=false) snaps into place to avoid a load flash.
    this.moveMagic(index, setFocus);

    if (setFocus) this.tabs[index].focus();

    this.dispatchEvent(
      new CustomEvent('featured-collections:tab-change', {
        bubbles: true,
        detail: { index, tab: this.tabs[index], panel: this.panels[index] },
      })
    );
  }

  onKeydown(event, currentIndex) {
    // Navigate only among tabs visible at the current viewport width.
    const visibleIndexes = this.tabs.map((tab, i) => i).filter((i) => this.isTabVisible(i));
    if (visibleIndexes.length === 0) return;

    const lastPos = visibleIndexes.length - 1;
    const currentPos = Math.max(0, visibleIndexes.indexOf(currentIndex));
    let nextPos = null;

    switch (event.key) {
      case 'ArrowRight':
        nextPos = currentPos === lastPos ? 0 : currentPos + 1;
        break;
      case 'ArrowLeft':
        nextPos = currentPos === 0 ? lastPos : currentPos - 1;
        break;
      case 'Home':
        nextPos = 0;
        break;
      case 'End':
        nextPos = lastPos;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activateTab(visibleIndexes[nextPos], true);
  }
}

if (!customElements.get('featured-collections-tabs')) {
  customElements.define('featured-collections-tabs', FeaturedCollectionsTabs);
}
